/**
 * Dependency-free SVG charts for the dashboard. Colours come from CSS variables
 * so light/dark themes and rebrands flow through automatically.
 */

import { CHANNEL_COLORS } from './colors';

const PRIMARY = CHANNEL_COLORS.NVS;
const GRID = 'var(--chart-grid)';

function niceMax(v: number): number {
  if (v <= 0) return 1;
  const mag = Math.pow(10, Math.floor(Math.log10(v)));
  const norm = v / mag;
  const step = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10;
  return step * mag;
}

interface LineSeries {
  name: string;
  color: string;
  points: number[];
}

/** Multi-line chart over shared x-categories. */
export function MultiLine({
  categories,
  series,
  format,
}: {
  categories: string[];
  series: LineSeries[];
  format: (v: number) => string;
}) {
  const W = 720;
  const H = 280;
  const padL = 56;
  const padR = 16;
  const padT = 16;
  const padB = 44;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const allVals = series.flatMap((s) => s.points);
  const maxVal = niceMax(Math.max(...allVals, 0));
  const x = (i: number) => padL + (categories.length <= 1 ? plotW / 2 : (plotW * i) / (categories.length - 1));
  const y = (v: number) => padT + plotH - (v / maxVal) * plotH;
  const ticks = 4;
  const labelEvery = Math.max(1, Math.ceil(categories.length / 6));

  return (
    <svg className="chart" viewBox={`0 0 ${W} ${H}`} role="img" preserveAspectRatio="xMidYMid meet">
      {Array.from({ length: ticks + 1 }, (_, i) => {
        const v = (maxVal / ticks) * i;
        return (
          <g key={i}>
            <line x1={padL} x2={W - padR} y1={y(v)} y2={y(v)} stroke={GRID} strokeWidth={1} />
            <text x={padL - 8} y={y(v) + 4} textAnchor="end" className="chart-axis-text">
              {format(v)}
            </text>
          </g>
        );
      })}

      {categories.map((c, i) =>
        i % labelEvery === 0 ? (
          <text key={c + i} x={x(i)} y={H - padB + 18} textAnchor="middle" className="chart-axis-text">
            {c}
          </text>
        ) : null,
      )}

      {series.map((s) => {
        const d = s.points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${y(p)}`).join(' ');
        return (
          <g key={s.name}>
            <path d={d} fill="none" stroke={s.color} strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" />
            {s.points.map((p, i) => (
              <circle key={i} cx={x(i)} cy={y(p)} r={3} fill={s.color}>
                <title>{`${s.name} · ${categories[i]}: ${format(p)}`}</title>
              </circle>
            ))}
          </g>
        );
      })}
    </svg>
  );
}

/** Single-series vertical bars with rotated category labels. */
export function SimpleBars({
  data,
  format,
  color = PRIMARY,
}: {
  data: Array<{ label: string; value: number }>;
  format: (v: number) => string;
  color?: string;
}) {
  const W = 720;
  const H = 320;
  const padL = 52;
  const padR = 16;
  const padT = 16;
  const padB = 96;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const maxVal = niceMax(Math.max(...data.map((d) => d.value), 0));
  const y = (v: number) => padT + plotH - (v / maxVal) * plotH;
  const slot = plotW / Math.max(1, data.length);
  const barW = Math.min(42, slot - 10);
  const ticks = 4;

  return (
    <svg className="chart" viewBox={`0 0 ${W} ${H}`} role="img" preserveAspectRatio="xMidYMid meet">
      {Array.from({ length: ticks + 1 }, (_, i) => {
        const v = (maxVal / ticks) * i;
        return (
          <g key={i}>
            <line x1={padL} x2={W - padR} y1={y(v)} y2={y(v)} stroke={GRID} strokeWidth={1} />
            <text x={padL - 8} y={y(v) + 4} textAnchor="end" className="chart-axis-text">
              {format(v)}
            </text>
          </g>
        );
      })}
      {data.map((d, i) => {
        const cx = padL + slot * i + slot / 2;
        return (
          <g key={d.label}>
            <rect x={cx - barW / 2} y={y(d.value)} width={barW} height={padT + plotH - y(d.value)} fill={color} rx={3}>
              <title>{`${d.label}: ${format(d.value)}`}</title>
            </rect>
            <text
              x={cx}
              y={H - padB + 14}
              textAnchor="end"
              className="chart-axis-text chart-cat-text"
              transform={`rotate(-35 ${cx} ${H - padB + 14})`}
            >
              {d.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/** Shared legend chip row. */
export function ChartLegend({ items }: { items: Array<{ label: string; color: string }> }) {
  return (
    <div className="chart-legend">
      {items.map((it) => (
        <span key={it.label} className="chart-legend-item">
          <span className="chart-legend-swatch" style={{ background: it.color }} aria-hidden="true" />
          {it.label}
        </span>
      ))}
    </div>
  );
}
